import { ApiClient } from "@/services/apiClient";
import { AxiosError, type AxiosResponse } from "axios";
import * as Umami from "@/services/analytics/umami";
import { type CollectionItem_ListItem, type ApiData_UpdateCollectionItem, type CollectionItem, type CollectionItemId, type CreateCollectionItemData, Api_ItemPatchOperation, type PublicCollectionItem_ListItem, type PublicCollectionItem, PublicItemNotFoundException } from "@/services/collection/item/types";
import { OnlyPublicProperties } from "@/types";
import { Filter as PortfolioFilter } from "@/services/portfolio";
import { PublicCollectionItemsFilter } from "@/services/collection/item";

/*
 * ===================================================================
 *
 * Collection items fetching.
 * eg. lists, details
 *
 * ===================================================================
 */

export async function fetchCollectionItemById(itemId: CollectionItemId): Promise<CollectionItem> {
  const url = "api/collection/item/" + itemId;
  return ApiClient.get<CollectionItem>(url).then(({ data }) => data);
}

/** @deprecated Use fetchCollectionItemById() */
export async function getCollectionItemById(itemId: CollectionItemId) {
  return fetchCollectionItemById(itemId);
}

export async function fetchPublicCollectionItemById(itemId: CollectionItemId): Promise<PublicCollectionItem> {
  const url = `api-public/user/collector-item`;
  const params = { id: itemId };
  return ApiClient.getPublic<PublicCollectionItem>(url, params)
    .then(({ data }) => data)
    .catch((error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        throw new PublicItemNotFoundException();
      } else {
        throw error;
      }
    });
}

type ApiResponse_FetchItemsList = {
  items: CollectionItem_ListItem[];
  totalCount: number;
  totalUnfilteredCount: number;
};

export const fetchPortfolioItems = async (filter: OnlyPublicProperties<PortfolioFilter.Filter>): Promise<ApiResponse_FetchItemsList> => {
  const url = "api/collection/item";
  const params = filter.createApiParams();
  return ApiClient.get<ApiResponse_FetchItemsList>(url, params).then((response) => response.data);
};

type ApiResponse_FeaturedItemList = {
  count: number;
  result: PublicCollectionItem_ListItem[];
};

export const fetchFeaturedCollectionItems = async (): Promise<ApiResponse_FeaturedItemList> => {
  const url = `api-public/user/collection-listed-item/top-pick`;
  return ApiClient.getPublic<ApiResponse_FeaturedItemList>(url, {}).then(({ data }) => data);
  // Intentionally no catch() ... Errors should be handled directly in items grid/list component.
};

type ApiResponse_PublicCollectionItemList = {
  count: number;
  result: PublicCollectionItem_ListItem[];
};

export const fetchPublicCollectionItems = async (filter: OnlyPublicProperties<PublicCollectionItemsFilter.Filter>): Promise<ApiResponse_PublicCollectionItemList> => {
  const url = `api-public/user/collection-listed-item/search`;
  const params = PublicCollectionItemsFilter.createApiParams(filter);
  return ApiClient.getPublic<ApiResponse_PublicCollectionItemList>(url, params).then(({ data }) => data);
  // Intentionally no catch() ... Errors should be handled directly in items grid/list component.
};

export const fetchPublicCollectionItemsForUser = async (filter: OnlyPublicProperties<PublicCollectionItemsFilter.Filter>): Promise<ApiResponse_PublicCollectionItemList> => {
  const url = `api-public/user/collection-listed-item`;
  const params = PublicCollectionItemsFilter.createApiParams(filter);
  return ApiClient.getPublic<ApiResponse_PublicCollectionItemList>(url, params).then(({ data }) => data);
  // Intentionally no catch() ... Errors should be handled directly in items grid/list component.
};

/*
 * ===================================================================
 *
 * Collection items creating.
 *
 * ===================================================================
 */

export async function createCollectionItem(data: CreateCollectionItemData): Promise<CollectionItem> {
  return ApiClient.post<CollectionItem>("api/collection/item", {}, data).then(({ data }) => data);
}

/*
 * ===================================================================
 *
 * Collection items deleting.
 *
 * ===================================================================
 */

export async function deleteCollectionItem(itemId: CollectionItemId): Promise<AxiosResponse> {
  const url = "api/collection/item/" + itemId;
  return ApiClient.delete(url).then((response) => {
    const umamiEvent: Umami.Event = { name: Umami.EventName.ItemDeleted };
    Umami.trackEvent(umamiEvent);
    return response;
  });
}

/*
 * ===================================================================
 *
 * Collection items manipulation.
 * eg. update, archivate, etc.
 *
 * ===================================================================
 */

export async function updateCollectionItem(itemId: CollectionItemId, data: ApiData_UpdateCollectionItem): Promise<CollectionItem> {
  const url = "api/collection/item/" + itemId;
  return ApiClient.put<CollectionItem>(url, {}, data).then(({ data }) => data);
}

export const archivateCollectionItem = async (itemId: CollectionItemId): Promise<AxiosResponse> => {
  const url = "api/collection/item/" + itemId;
  const params = { operation: Api_ItemPatchOperation.Archivate };
  return ApiClient.patch(url, {}, params);
};

export const requestCollectionItemOwnershipTransfer = async (itemId: CollectionItemId, newOwnerNickname: string): Promise<AxiosResponse> => {
  const url = "api/collection/item/" + itemId;
  const params = {
    operation: Api_ItemPatchOperation.RequestOwnershipTransfer,
    newOwnerNickname,
  };
  return ApiClient.patch(url, {}, params);
};
